import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useSiteClub } from "./SiteClubContext";

// A club's custom CSS (Manage -> Advanced settings), applied on its own
// domain only. The CSS is wrapped as `.club-theme { <css> }` using CSS
// nesting, so top-level declarations (`--accent: #0055ff;`) restyle the club
// site and nested rules only match inside the wrapper. The backend
// (lib/clubCss.ts) rejects url(), @import, "<", backslashes and unbalanced
// braces, so it can't load anything or escape the wrapper.
//
// Never applied where a lookalike or hidden element could do harm (sign-in,
// checkout, account) or where broken CSS would lock a manager out of fixing
// it (Manage, Admin).

const UNTHEMED_PREFIXES = ["/login", "/auth", "/cart", "/account", "/admin", "/manage"];

export function themeAllowedOn(pathname: string): boolean {
  return !UNTHEMED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** Wraps `css` for `scope` (a class selector, e.g. ".club-theme"). */
export function scopeCss(scope: string, css: string): string {
  return `${scope} {\n${css}\n}\n`;
}

/** Injects `css` scoped to `scope` as a <style> element while mounted. Uses
 *  textContent, so nothing in it is parsed as HTML. */
export function useScopedStyle(id: string, scope: string, css: string | null | undefined): void {
  useEffect(() => {
    if (!css) return;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = scopeCss(scope, css);
    document.head.appendChild(el);
    return () => el.remove();
  }, [id, scope, css]);
}

/** The app's outer wrapper: on a club's site (outside the pages above) it
 *  carries `.club-theme` and the club's CSS. */
export function ClubThemeRoot({ children }: { children: ReactNode }) {
  const { club } = useSiteClub();
  const { pathname } = useLocation();
  const css = club && themeAllowedOn(pathname) ? club.customCss : "";
  useScopedStyle("club-custom-css", ".club-theme", css);
  return <div className={css ? "club-theme" : undefined}>{children}</div>;
}
